// src/features/streams/simulcast/simulcastError.ts

import {
  SimulcastType,
  SimulcastErrorAction,
  type ISimulcastAllData,
  type IExtPlatAuthView,
} from '@/lib/types/Simulcast';
import { FacebookUtil } from '@lib2/model/extplat/facebook/Facebook';
import { YoutubeUtil } from '@lib2/model/extplat/youtube/Youtube';

export interface DestinationErrorState {
  action: SimulcastErrorAction;
  message: string;
  code: string | null;
  source: 'extplat' | 'simulcast';
  created: number | null;
}

/**
 * Error normalization and action resolution for simulcast destinations.
 * Ported from legacy SimulcastView error logic with centralized resolution.
 */
export class SimulcastErrorUtils {
  /**
   * Get the normalized error state for a simulcast destination.
   * Resolution priority (from legacy):
   * 1. FB/YT with missing extPlatAuthId or missing auth record → Delete
   * 2. FB/YT with extPlatAuth error → takes precedence over simulcast errors
   * 3. FB simulcast error → FacebookUtil.getFBErrorAction()
   * 4. YT simulcast error → YoutubeUtil.getYTErrorAction()
   * 5. RTMP: HTTP 400 → Reconnect; other codes → TryAgainLater
   * 6. No error → null
   */
  static getErrorState(
    simulcast: ISimulcastAllData,
    extPlatAuth?: IExtPlatAuthView,
    authsLoaded = true,
  ): DestinationErrorState | null {
    const { core } = simulcast;

    switch (core.simulcastType) {
      case SimulcastType.Facebook:
        return this.getFBError(simulcast, extPlatAuth, authsLoaded);

      case SimulcastType.Youtube:
        return this.getYTError(simulcast, extPlatAuth, authsLoaded);

      case SimulcastType.Rtmp:
        return this.getRtmpError(simulcast);

      default:
        return null;
    }
  }

  /** Whether the error state should disable start/stop controls. */
  static shouldDisableControls(errorState: DestinationErrorState | null): boolean {
    if (!errorState) return false;
    return errorState.action === SimulcastErrorAction.Reconnect
      || errorState.action === SimulcastErrorAction.Delete;
  }

  // --- Private helpers ---

  private static getFBError(simulcast: ISimulcastAllData, extPlatAuth?: IExtPlatAuthView, authsLoaded = true): DestinationErrorState | null {
    const { core } = simulcast;

    // No extPlatAuthId at all → genuinely missing, always show
    if (!core.extPlatAuthId) {
      return {
        action: SimulcastErrorAction.Delete,
        message: 'Facebook authorization is missing. Delete this destination and recreate it.',
        code: null,
        source: 'extplat',
        created: null,
      };
    }

    // Auth data still loading → suppress until we know for sure
    if (!authsLoaded && !extPlatAuth) return null;

    // Auth record not found after loading → genuinely missing
    if (!extPlatAuth) {
      return {
        action: SimulcastErrorAction.Delete,
        message: 'Facebook authorization is missing. Delete this destination and recreate it.',
        code: null,
        source: 'extplat',
        created: null,
      };
    }

    // ExtPlatAuth has error → precedence over simulcast error
    if (extPlatAuth.lastErrorCode && extPlatAuth.lastErrorMsg) {
      return {
        action: FacebookUtil.getFBErrorAction(extPlatAuth.lastErrorCode),
        message: extPlatAuth.lastErrorMsg,
        code: extPlatAuth.lastErrorCode,
        source: 'extplat',
        created: extPlatAuth.updated,
      };
    }

    // Simulcast-level error
    return this.getSimulcastError(simulcast, (code) => FacebookUtil.getFBErrorAction(code));
  }

  private static getYTError(simulcast: ISimulcastAllData, extPlatAuth?: IExtPlatAuthView, authsLoaded = true): DestinationErrorState | null {
    const { core } = simulcast;

    // No extPlatAuthId at all → genuinely missing, always show
    if (!core.extPlatAuthId) {
      return {
        action: SimulcastErrorAction.Delete,
        message: 'YouTube authorization is missing. Delete this destination and recreate it.',
        code: null,
        source: 'extplat',
        created: null,
      };
    }

    // Auth data still loading → suppress until we know for sure
    if (!authsLoaded && !extPlatAuth) return null;

    // Auth record not found after loading → genuinely missing
    if (!extPlatAuth) {
      return {
        action: SimulcastErrorAction.Delete,
        message: 'YouTube authorization is missing. Delete this destination and recreate it.',
        code: null,
        source: 'extplat',
        created: null,
      };
    }

    // ExtPlatAuth has error → precedence over simulcast error
    if (extPlatAuth.lastErrorCode && extPlatAuth.lastErrorMsg) {
      return {
        action: YoutubeUtil.getYTErrorAction(extPlatAuth.lastErrorCode),
        message: extPlatAuth.lastErrorMsg,
        code: extPlatAuth.lastErrorCode,
        source: 'extplat',
        created: extPlatAuth.updated,
      };
    }

    // Simulcast-level error
    return this.getSimulcastError(simulcast, (code) => YoutubeUtil.getYTErrorAction(code));
  }

  private static getRtmpError(simulcast: ISimulcastAllData): DestinationErrorState | null {
    return this.getSimulcastError(simulcast, (code) =>
      code === '400' ? SimulcastErrorAction.Reconnect : SimulcastErrorAction.TryAgainLater,
    );
  }

  private static getSimulcastError(
    simulcast: ISimulcastAllData,
    resolveAction: (code: string) => SimulcastErrorAction,
  ): DestinationErrorState | null {
    const { core } = simulcast;

    if (core.lastErrorHttpStatusCode && core.lastErrorSafeMsg && core.lastErrorCreated) {
      const code = core.lastErrorHttpStatusCode.toString();
      return {
        action: resolveAction(code),
        message: core.lastErrorSafeMsg,
        code,
        source: 'simulcast',
        created: core.lastErrorCreated,
      };
    }

    return null;
  }
}
